// Small "capsule ready" audio cue: a soft two-note chime via WebAudio,
// optionally followed by a spoken announcement via speechSynthesis when
// the user is in audio mode.
//
// iOS Safari won't produce sound unless the AudioContext / speechSynthesis
// is unlocked inside a user-gesture handler. `primeAudioOnGesture()` MUST
// be called from a click/touch handler (e.g. the Generate button) before
// the capsule flow starts. The primed context is reused for playReadyChime.

const IS_DEV = process.env.NODE_ENV === "development";
const debug = (...args) => { if (IS_DEV) console.debug("[chime]", ...args); };

let _sharedCtx = null;
let _primed = false;

function _getAC() {
  return window.AudioContext || window.webkitAudioContext || null;
}

/**
 * Call inside a click/touch handler to unlock audio on iOS Safari.
 * Safe to call repeatedly — no-ops after the first successful prime.
 */
export function primeAudioOnGesture() {
  try {
    const AC = _getAC();
    if (AC && !_sharedCtx) _sharedCtx = new AC();
    if (_sharedCtx && _sharedCtx.state === "suspended") {
      _sharedCtx.resume().catch((err) => debug("resume rejected:", err?.message || err));
    }
    // Play a nearly-silent one-sample tick so iOS marks the context as unlocked.
    if (_sharedCtx && !_primed) {
      const o = _sharedCtx.createOscillator();
      const g = _sharedCtx.createGain();
      g.gain.value = 0.00001;
      o.connect(g).connect(_sharedCtx.destination);
      o.start();
      o.stop(_sharedCtx.currentTime + 0.02);
    }
    // Also prime speechSynthesis with a silent utterance on iOS.
    if (typeof window !== "undefined" && "speechSynthesis" in window && !_primed) {
      const u = new SpeechSynthesisUtterance("");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
    _primed = true;
  } catch (err) {
    debug("prime failed:", err?.message || err);
  }
}

function twoNoteChime(ctx) {
  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.28, now + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.9);
  master.connect(ctx.destination);

  const play = (freq, offset, dur) => {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, now + offset);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, now + offset);
    g.gain.exponentialRampToValueAtTime(0.9, now + offset + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, now + offset + dur);
    osc.connect(g).connect(master);
    osc.start(now + offset);
    osc.stop(now + offset + dur + 0.02);
  };
  play(660, 0, 0.35);     // E5
  play(880, 0.20, 0.40);  // A5
}

export async function playReadyChime({ speak = false, message } = {}) {
  try {
    const AC = _getAC();
    if (!AC) return;
    // Reuse the primed context if available so iOS gesture-unlock persists.
    let ctx = _sharedCtx;
    if (!ctx) {
      ctx = new AC();
      _sharedCtx = ctx;
    }
    if (ctx.state === "suspended") {
      try { await ctx.resume(); } catch (err) { debug("resume in playReadyChime failed:", err?.message || err); }
    }
    twoNoteChime(ctx);
  } catch (err) {
    debug("playReadyChime tone failed:", err?.message || err);
  }

  if (speak && typeof window !== "undefined" && "speechSynthesis" in window) {
    try {
      // Small delay so the chime finishes before speech starts.
      setTimeout(() => {
        try {
          // iOS quirk: cancel any queued silent-prime utterance first.
          window.speechSynthesis.cancel();
        } catch (err) { debug("speechSynthesis.cancel failed:", err?.message || err); }
        const u = new SpeechSynthesisUtterance(message || "Your daily capsule is ready.");
        u.rate = 1.0;
        u.pitch = 1.0;
        u.volume = 0.9;
        window.speechSynthesis.speak(u);
      }, 700);
    } catch (err) {
      debug("speechSynthesis.speak failed:", err?.message || err);
    }
  }
}
